import L from 'leaflet';
import { SOURCES } from '../config.js';
import { parseGmlRings } from './sites.js';
import sitesData from '../../data/sites.json';

// Masterplan zones drawn on the REAL parcel geometry. Each site's outline is
// fetched from the Catastro INSPIRE WFS (same endpoint as the sites layer);
// program zones are then carved out of that outline by clipping it against
// band rectangles, so every zone edge on the perimeter follows the official
// cadastral boundary. If Catastro is unreachable, the hand-drawn footprint
// from sites.json is used as the clipping subject instead.

const ZONE_STYLES = {
  roads: { color: '#2d3748', weight: 3, fillColor: '#4a5568', fillOpacity: 0.6 },
  residential: { color: '#ea580c', weight: 2, fillColor: '#f97316', fillOpacity: 0.5 },
  hotel: { color: '#1e40af', weight: 2, fillColor: '#1e40af', fillOpacity: 0.45 },
  vpp: { color: '#be185d', weight: 2, fillColor: '#ec4899', fillOpacity: 0.4 },
  equestrian: { color: '#b8860b', weight: 2, fillColor: '#d4af37', fillOpacity: 0.35 },
  green: { color: '#65a30d', weight: 1.5, fillColor: '#86efac', fillOpacity: 0.25, dashArray: '4 2' },
};

const SITES = Object.fromEntries(sitesData.sites.map((s) => [s.id, s]));

// ---------------------------------------------------------------------------
// Polygon clipping (Sutherland–Hodgman against an axis-aligned rectangle).
// Rings are [lat, lng] arrays; rects are {latMin, latMax, lngMin, lngMax}.

function clipHalfPlane(ring, inside, intersect) {
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    const cur = ring[i];
    const prev = ring[(i + ring.length - 1) % ring.length];
    const curIn = inside(cur);
    if (curIn !== inside(prev)) out.push(intersect(prev, cur));
    if (curIn) out.push(cur);
  }
  return out;
}

function clipRingToRect(ring, r) {
  let poly = ring;
  const planes = [
    [(p) => p[0] >= r.latMin, 0, r.latMin],
    [(p) => p[0] <= r.latMax, 0, r.latMax],
    [(p) => p[1] >= r.lngMin, 1, r.lngMin],
    [(p) => p[1] <= r.lngMax, 1, r.lngMax],
  ];
  for (const [inside, axis, val] of planes) {
    if (poly.length < 3) return [];
    poly = clipHalfPlane(poly, inside, (a, b) => {
      const t = (val - a[axis]) / (b[axis] - a[axis]);
      return axis === 0
        ? [val, a[1] + t * (b[1] - a[1])]
        : [a[0] + t * (b[0] - a[0]), val];
    });
  }
  return poly.length >= 3 ? poly : [];
}

function bbox(rings) {
  let latMin = Infinity, latMax = -Infinity, lngMin = Infinity, lngMax = -Infinity;
  for (const ring of rings) {
    for (const [lat, lng] of ring) {
      if (lat < latMin) latMin = lat;
      if (lat > latMax) latMax = lat;
      if (lng < lngMin) lngMin = lng;
      if (lng > lngMax) lngMax = lng;
    }
  }
  return { latMin, latMax, lngMin, lngMax };
}

// A rect spanning the given fractions of the bbox (lat 0 = south, lng 0 = west).
function band(b, latFrom, latTo, lngFrom, lngTo) {
  const dLat = b.latMax - b.latMin;
  const dLng = b.lngMax - b.lngMin;
  return {
    latMin: b.latMin + dLat * latFrom,
    latMax: b.latMin + dLat * latTo,
    lngMin: b.lngMin + dLng * lngFrom,
    lngMax: b.lngMin + dLng * lngTo,
  };
}

// ---------------------------------------------------------------------------

function zonePopup(z) {
  const gate = z.phaseGate
    ? `<div style="background:#fca5a5;color:#7f1d1d;padding:4px 8px;border-radius:4px;font-size:11px;margin:8px 0"><b>${z.phaseGate}</b></div>`
    : '';
  return `
    <div style="font-size:12px;max-width:230px">
      <b>${z.name}</b> (${z.id})
      <div style="color:#555;font-size:11px;margin:4px 0">
        ${z.areaM2.toLocaleString('en')} m² · ${z.builtM2 ? `${z.builtM2.toLocaleString('en')} m² built` : 'open space'}
      </div>
      <div style="margin:6px 0;line-height:1.4;font-size:11px">${z.program}</div>
      ${gate}
      <div style="margin-top:6px;font-size:10px;color:#888"><code>${z.cteRef}</code></div>
    </div>`;
}

// Boalo — one large parcel carved into program bands. Fractions are placed by
// the design logic: VPP at the NW corner nearest the village road, hotel core
// hugging the western access edge, residences on the S/SE mid-slope contours,
// equestrian meadow on the flat east, dehesa commons = the whole remainder.
const BOALO_BANDS = [
  { latFrom: 0, latTo: 1, lngFrom: 0, lngTo: 1, type: 'green', z: {
    id: 'Z5', name: 'Dehesa commons', areaM2: 52863, builtM2: 0,
    program: '75% of the parcel held open: trail loop on the stone-wall spines, pozas path, water reserve for hydrants, dark-sky zone. Managed grazing; no lighting beyond bollards.',
    phaseGate: 'P0 FOUNDATIONAL: verify PRCAM/PNSG protected-land status; obtain favorable Consejería opinion (est. 12–18 months).',
    cteRef: 'PRCAM PORN · P.N. Guadarrama PRUG/ZPP · CTE DB-SI wildland-urban interface',
  } },
  { latFrom: 0.65, latTo: 1, lngFrom: 0, lngTo: 0.3, type: 'vpp', z: {
    id: 'Z3', name: 'VPP village edge', areaM2: 4500, builtM2: 3524,
    program: 'Regulated housing as a compact 2–3 storey piece on the corner nearest the village/road — connected to town, not embedded in the resort core. 39.5% of the residential component.',
    phaseGate: 'P3 GATED: delivery per instrument conditions.',
    cteRef: 'Ley 9/2001 (VPP ≥30% of residential edificability) · NNSS El Boalo heights',
  } },
  { latFrom: 0.2, latTo: 0.65, lngFrom: 0, lngTo: 0.32, type: 'hotel', z: {
    id: 'Z1', name: 'Arrival & core', areaM2: 14000, builtM2: 12158,
    program: '44-key landscape hotel + spa & farm-to-table restaurant + subterranean parking. Compact cluster at the western access, screened by the first stone wall.',
    phaseGate: 'P2 CONDITIONAL: hotel/spa only upon sector approval (Consejería de Medio Ambiente, PNSG overlay).',
    cteRef: 'NNSS El Boalo 1997/98 · sector ordinance required for hotel volume · CTE DB-SI water reserve',
  } },
  { latFrom: 0.15, latTo: 0.75, lngFrom: 0.32, lngTo: 0.68, type: 'residential', z: {
    id: 'Z2', name: 'Residence contours', areaM2: 16000, builtM2: 5392,
    program: '~27 branded residences (Echo / Duo / Grand) on S/SE contour lines at 25–40 m spacing, single storey on stilts, no fences, no excavation.',
    phaseGate: 'P1 ACTIVE: precedent-scale residential first (15–25 units near village edge) — funds the hold.',
    cteRef: 'NNSS B+1 / ~6.5–7 m unifamiliar heights · CTE all DBs · defensible-space strip per unit',
  } },
  { latFrom: 0.2, latTo: 0.7, lngFrom: 0.68, lngTo: 1, type: 'equestrian', z: {
    id: 'Z4', name: 'Equestrian & farm meadow', areaM2: 6000, builtM2: 300,
    program: 'Equestrian centre + kitchen garden on the flat east meadow; light timber structures only.',
    phaseGate: 'P3: activation after core phases.',
    cteRef: 'CTE timber structural DBs · wildfire defensibility',
  } },
];

// Colmenarejo — the villa lots ARE the cadastral parcels, so each fetched
// parcel is rendered in its program color directly (no banding needed).
const COLMENAREJO_URBAN = {
  id: 'Z1', name: 'Villa parcel (candidate lot)', areaM2: 4700, builtM2: 1160,
  program: 'U.E. 18 lot fronting the Cerca del Pino sewer axis — candidate for a BYLD Duo Lounge / Grand 2-storey modular villa (~290 m² over 2 floors, 900 m²+ garden). Sale portion is a subset of the unit — confirm against nota simple + segregation licence.',
  phaseGate: null,
  cteRef: 'NNSS 1996 Zona 03 grado 3º: ≥1,000 m² · frente ≥20 m · ocupación ≤25% · 0.25 m²/m² · 6.50 m cornisa · retranqueos 4/4/3',
};
const COLMENAREJO_RUSTIC = {
  id: 'Z3', name: 'Green edge (rústico)', areaM2: 3948, builtM2: 0,
  program: 'Edge parcel in suelo no urbanizable toward the Galapagar dehesa — borrowed landscape for the villas; the unit’s 3,948 m² zona-verde cession lands nearby «entre calles».',
  phaseGate: null,
  cteRef: 'SNU — no edificability · Ley 9/2001 cessions per ficha U.E. 18',
};

// ---------------------------------------------------------------------------
// Boalo — surrounding rustico común parcels (polígonos 5 & 9), all available
// for purchase per the owner. Areas VERIFIED via Catastro INSPIRE WFS
// (15-Jul-2026). Point-in-polygon against the CM PORN zonification shows the
// estate area sits OUTSIDE the PRCAM (nearest zone B1 ~410 m NE), so the
// binding regime is Ley 9/2001 art. 29: calificación urbanística on SNU común
// — max 10% ocupación, 4.5 m to eaves, uses linked to an agro/equestrian
// exploitation. NO residential subdivision. Overlays still to verify per
// parcel: vías pecuarias, montes preservados, DPH.
// NOTE: 28023A00500006 was in the owner's list but Catastro returns «parcela
// no encontrada» — likely renumbered or merged; ask for the nota simple.
const RUSTICO_GATE =
  'GATED: Calificación Urbanística (Ley 9/2001 art. 29, CM) required BEFORE municipal license. Uses must be linked to the agro/equestrian exploitation; no residential subdivision on SNU.';
const RUSTICO_REF =
  'Ley 9/2001 art. 29 (mod. Ley 7/2024) · ≤10% ocupación · ≤4.5 m alero · Decreto 48/2023 (hotel rural ≤50 keys) · RD 804/2011 (equine welfare)';

export const RUSTICO_PARCELS = [
  { rc: '28023A00500001', areaM2: 85739, type: 'equestrian', z: {
    id: 'R1', name: 'Equestrian core (rústico)', areaM2: 85739, builtM2: 4150,
    program: 'Primary exploitation: stable block ~2,400 m² (60–80 boxes at 3.0×3.5 m), covered arena ~1,200 m², feed & tack ~400 m², ONE accessory manager dwelling ~150 m² (employment-linked, discretionary). 4.8% of the 8,574 m² occupation ceiling.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500047', areaM2: 57095, type: 'hotel', z: {
    id: 'R2', name: 'Agritourism hotel (rústico)', areaM2: 57095, builtM2: 2900,
    program: 'Hotel rural ~30 keys ≈ 2,400 m² GFA + farm-to-table restaurant/spa ~500 m² (Decreto 48/2023: 2–50 keys). Tied to the equestrian/farm exploitation — amenity lodging, not a standalone resort. 5.1% vs the 5,710 m² ceiling. Parking ≥30% of keys.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500043', areaM2: 55545, type: 'residential', z: {
    id: 'R3', name: 'Farm & bodega (rústico)', areaM2: 55545, builtM2: 1450,
    program: 'Working farm hub: bodega/processing ~1,200 m² + farm shop & tasting ~250 m² (own production only). 2.6% vs the 5,555 m² ceiling. Removable silos/greenhouses exempt from calificación (Ley 7/2024).',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500061', areaM2: 53117, type: 'green', z: {
    id: 'R4', name: 'Vineyard / olive grove (rústico)', areaM2: 53117, builtM2: 15,
    program: 'Planted production land feeding the bodega and restaurant. Only exempt structures: tool shed ≤15 m², removable irrigation and netting. Counts toward the exploitation’s agrarian-use justification.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500044', areaM2: 41059, type: 'green', z: {
    id: 'R5', name: 'Vineyard / crops (rústico)', areaM2: 41059, builtM2: 15,
    program: 'Production land — vines/olives/market garden. Exempt structures only (shed ≤15 m², removable greenhouses).',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500046', areaM2: 40400, type: 'green', z: {
    id: 'R6', name: 'Pasture / hay meadow (rústico)', areaM2: 40400, builtM2: 0,
    program: 'Grazing and hay for the equestrian core; removable shelters and water troughs only (exempt, Ley 7/2024).',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00509004', areaM2: 32524, type: 'green', z: {
    id: 'R7', name: 'Paddocks (rústico, polígono 9)', areaM2: 32524, builtM2: 0,
    program: 'Turnout paddocks and trail link; perimeter fencing and removable field shelters only.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500010', areaM2: 28038, type: 'green', z: {
    id: 'R8', name: 'Paddocks / trails (rústico)', areaM2: 28038, builtM2: 0,
    program: 'Below the 3 ha secano minimum on its own — aggregate into the single registered exploitation. Grazing + bridle-path loop.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500004', areaM2: 13798, type: 'green', z: {
    id: 'R9', name: 'Meadow (rústico)', areaM2: 13798, builtM2: 0,
    program: 'Below agrarian unit minimums individually — pasture/trails within the aggregated exploitation; no standalone building rights.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500005', areaM2: 14795, type: 'green', z: {
    id: 'R10', name: 'Meadow (rústico)', areaM2: 14795, builtM2: 0,
    program: 'Below agrarian unit minimums individually — pasture/trails within the aggregated exploitation; no standalone building rights.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
  { rc: '28023A00500007', areaM2: 230, type: 'green', z: {
    id: 'R11', name: 'Sliver parcel (rústico)', areaM2: 230, builtM2: 0,
    program: '230 m² sliver — access/utility point (well or track). No program; verify title role in the nota simple.',
    phaseGate: RUSTICO_GATE, cteRef: RUSTICO_REF,
  } },
];

// Dashed outline = suelo no urbanizable: same program palette, SNU signature.
function rusticoStyle(type) {
  return { ...ZONE_STYLES[type], dashArray: '6 4', fillOpacity: (ZONE_STYLES[type].fillOpacity ?? 0.4) * 0.6 };
}

// Draws the surrounding rustico parcels (real INSPIRE geometry) with their
// theoretical program envelopes. Shared with the micro-parcels layer so the
// surround shows under both program views ("Volumes" and "Masterplans").
export function addRusticoParcels(group) {
  for (const spec of RUSTICO_PARCELS) {
    fetchParcelRings(spec.rc)
      .then((rings) => {
        group.addLayer(L.polygon(rings, rusticoStyle(spec.type))
          .bindPopup(zonePopup({ ...spec.z, name: `${spec.z.name} · ${spec.rc}` }), { maxWidth: 250 }));
      })
      .catch((e) => console.warn(`[masterplan] rustico ${spec.rc}:`, e.message));
  }
}

export function fetchParcelRings(rc) {
  return fetch(SOURCES.cadastreParcelWfs.replace('{rc}', rc))
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`Catastro ${r.status}`))))
    .then((gml) => {
      const rings = parseGmlRings(gml);
      if (!rings.length) throw new Error('no geometry');
      return rings;
    });
}

// Single source of truth for the Boalo site geometry, shared by every layer
// that draws on the estate (masterplan zones, micro-parcels). Memoized so the
// WFS is hit once; resolves to the official parcel rings or, failing that,
// the hand-drawn footprint.
// Memoized parcel geometry per site, so the pixel engine (and any layer that
// draws on the plot) fetch Catastro once. Rings come from the site's
// `masterplanRc` if set (a single parcel whose polygon defines the plan area),
// else the first cadastral ref, else the hand-drawn footprint fallback. This
// is what makes the masterplan engine run on ANY site by RC.
const siteRingsPromises = new Map();
export function getSiteRings(site) {
  if (!site) return Promise.resolve([]);
  if (!siteRingsPromises.has(site.id)) {
    const rc = site.masterplanRc ?? (site.useFootprintForPlan ? null : site?.cadastre?.refs?.[0]?.rc);
    const p = (rc ? fetchParcelRings(rc) : Promise.reject(new Error('no rc / footprint-based')))
      .catch((e) => {
        console.warn(`[${site.id}] Catastro geometry unavailable, using footprint:`, e.message);
        return site?.footprint ? [site.footprint] : [];
      });
    siteRingsPromises.set(site.id, p);
  }
  return siteRingsPromises.get(site.id);
}

// Back-compat wrapper — the Volumes layer and sites layer still call this.
export function getBoaloRings() {
  return getSiteRings(SITES['boalo-estate']);
}

function addBoaloZones(group, rings) {
  const b = bbox(rings);
  for (const spec of BOALO_BANDS) {
    const rect = band(b, spec.latFrom, spec.latTo, spec.lngFrom, spec.lngTo);
    const clipped = rings.map((ring) => clipRingToRect(ring, rect)).filter((r) => r.length);
    if (!clipped.length) continue;
    group.addLayer(L.polygon(clipped, ZONE_STYLES[spec.type])
      .bindPopup(zonePopup(spec.z), { maxWidth: 250 }));
  }
}

export default {
  id: 'overlay-masterplan',
  label: 'Masterplan zones',
  group: 'overlay',
  enabled: false,
  create() {
    const group = L.layerGroup();

    // --- Boalo: shared site geometry, banded into zones. ------------------
    getBoaloRings().then((rings) => { if (rings.length) addBoaloZones(group, rings); });

    // --- Colmenarejo: color each real parcel by its program role. --------
    const colme = SITES['colmenarejo-ue18'];
    let anyParcel = false;
    for (const ref of colme?.cadastre?.refs || []) {
      const rustic = /^\d{5}[A-Z]/.test(ref.rc); // rústico format: 5-digit municipality code + sector letter
      const meta = rustic ? COLMENAREJO_RUSTIC : COLMENAREJO_URBAN;
      fetchParcelRings(ref.rc)
        .then((rings) => {
          anyParcel = true;
          const z = { ...meta, areaM2: ref.areaM2 || meta.areaM2, name: `${meta.name} · ${ref.label}` };
          group.addLayer(L.polygon(rings, ZONE_STYLES[rustic ? 'green' : 'residential'])
            .bindPopup(zonePopup(z), { maxWidth: 250 }));
        })
        .catch((e) => console.warn(`[masterplan] ${ref.rc}:`, e.message));
    }
    // Fallback footprint if no parcel loads (offline): one residential blob.
    setTimeout(() => {
      if (!anyParcel && colme?.footprint) {
        group.addLayer(L.polygon(colme.footprint, ZONE_STYLES.residential)
          .bindPopup(zonePopup(COLMENAREJO_URBAN), { maxWidth: 250 }));
      }
    }, 8000);

    // --- Boalo: surrounding rustico parcels, each in its program role. ----
    addRusticoParcels(group);

    return group;
  },
};
